
import { isWindows } from "./Bridge.js";
import { isLetter } from "../fable_modules/fable-library-js.5.13.0/Char.js";
import { ofArray, tail, head as head_1, isEmpty, cons, empty, fold as fold_1, reverse } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { trimEnd, join as join_1, split, substring } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { equals } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { forAll } from "../fable_modules/fable-library-js.5.13.0/Seq.js";

const isWin = isWindows;

export const sepChar = isWin ? "\\" : "/";

const sepStr = sepChar;

const splitChars = isWin ? ["/", "\\"] : ["/"];

export function isSeparator(c) {
    if (c === "/") {
        return true;
    }
    else if (isWin) {
        return c === "\\";
    }
    else {
        return false;
    }
}

function skipSeps(p_mut, i_mut) {
    skipSeps:
    while (true) {
        const p = p_mut, i = i_mut;
        if ((i < p.length) && isSeparator(p[i])) {
            p_mut = p;
            i_mut = (i + 1);
            continue skipSeps;
        }
        else {
            return i | 0;
        }
        break;
    }
}

function untilSep(p_mut, i_mut) {
    untilSep:
    while (true) {
        const p = p_mut, i = i_mut;
        if ((i < p.length) && !isSeparator(p[i])) {
            p_mut = p;
            i_mut = (i + 1);
            continue untilSep;
        }
        else {
            return i | 0;
        }
        break;
    }
}

function rootLength(p) {
    const n = p.length | 0;
    if (((isWin && (n >= 2)) && (p[1] === ":")) && isLetter(p[0])) {
        return skipSeps(p, 2) | 0;
    }
    else if (((isWin && (n >= 2)) && isSeparator(p[0])) && isSeparator(p[1])) {
        const serverStart = skipSeps(p, 0) | 0;
        const serverEnd = untilSep(p, serverStart) | 0;
        const shareStart = skipSeps(p, serverEnd) | 0;
        const shareEnd = untilSep(p, shareStart) | 0;
        if ((serverEnd > serverStart) && (shareEnd > shareStart)) {
            return skipSeps(p, shareEnd) | 0;
        }
        else {
            return 1;
        }
    }
    else if ((n >= 1) && isSeparator(p[0])) {
        return skipSeps(p, 0) | 0;
    }
    else {
        return 0;
    }
}

function driveRootLength(p) {
    if (((isWin && (p.length >= 2)) && (p[1] === ":")) && isLetter(p[0])) {
        return 2;
    }
    else {
        return 0;
    }
}

/**
 * True when the path starts from a root rather than from wherever the process happens to be.
 * A bare drive is not absolute: "C:" and "C:a" are both relative to that drive's working directory.
 */
export function isAbsolute(p) {
    const n = p.length | 0;
    if (isWin) {
        if ((n >= 1) && isSeparator(p[0])) {
            return true;
        }
        else if (((n >= 3) && (p[1] === ":")) && isLetter(p[0])) {
            return isSeparator(p[2]);
        }
        else {
            return false;
        }
    }
    else if (n >= 1) {
        return p[0] === "/";
    }
    else {
        return false;
    }
}

function collapse(rooted, segs) {
    return reverse(fold_1((acc, seg) => {
        switch (seg) {
            case "":
            case ".":
                return acc;
            case "..": {
                let matchResult, prev_1, rest_1, acc_1;
                if (isEmpty(acc)) {
                    if (rooted) {
                        matchResult = 1;
                    }
                    else {
                        matchResult = 2;
                        acc_1 = acc;
                    }
                }
                else if (head_1(acc) !== "..") {
                    matchResult = 0;
                    prev_1 = head_1(acc);
                    rest_1 = tail(acc);
                }
                else {
                    matchResult = 2;
                    acc_1 = acc;
                }
                switch (matchResult) {
                    case 0:
                        return rest_1;
                    case 1:
                        return empty();
                    default:
                        return cons("..", acc_1);
                }
            }
            default:
                return cons(seg, acc);
        }
    }, empty(), segs));
}

/**
 * node's path.normalize: collapse `.` and `..`, squeeze repeated separators, and write the
 * platform's separator throughout. A trailing separator is kept, as node keeps it.
 */
export function normalise(p) {
    if (p === "") {
        return ".";
    }
    else {
        const rLen = rootLength(p) | 0;
        let root;
        if (rLen === 0) {
            root = "";
        }
        else if (((isWin && (rLen >= 2)) && (p[1] === ":")) && isLetter(p[0])) {
            root = ((rLen > 2) ? (substring(p, 0, 2) + sepStr) : substring(p, 0, 2));
        }
        else if (((isWin && (rLen >= 2)) && isSeparator(p[0])) && isSeparator(p[1])) {
            const serverStart = skipSeps(p, 0) | 0;
            const serverEnd = untilSep(p, serverStart) | 0;
            const shareStart = skipSeps(p, serverEnd) | 0;
            const shareEnd = untilSep(p, shareStart) | 0;
            root = (((serverEnd > serverStart) && (shareEnd > shareStart)) ? (((((sepStr + sepStr) + substring(p, serverStart, serverEnd - serverStart)) + sepStr) + substring(p, shareStart, shareEnd - shareStart)) + sepStr) : sepStr);
        }
        else {
            root = sepStr;
        }
        const segs_1 = collapse(rLen > 0, ofArray(split(substring(p, rLen), splitChars)));
        const body = join_1(sepStr, segs_1);
        const trailing = (!equals(segs_1, empty()) && isSeparator(p[p.length - 1])) ? sepStr : "";
        if (root === "") {
            if (body === "") {
                return ".";
            }
            else {
                return body + trailing;
            }
        }
        else if (body === "") {
            if (root.endsWith(sepStr)) {
                return root;
            }
            else {
                return root + ".";
            }
        }
        else {
            return (root + body) + trailing;
        }
    }
}

/**
 * node's path.join: empty parts are dropped, the rest are joined and the result normalised.
 */
export function join(parts) {
    const joined = join_1(sepStr, parts.filter((s) => (s !== "")));
    if (joined === "") {
        return ".";
    }
    else {
        return normalise(joined);
    }
}

function lastSepIndex(s_mut, i_mut) {
    lastSepIndex:
    while (true) {
        const s = s_mut, i = i_mut;
        if (i < 0) {
            return -1;
        }
        else if (isSeparator(s[i])) {
            return i | 0;
        }
        else {
            s_mut = s;
            i_mut = (i - 1);
            continue lastSepIndex;
        }
        break;
    }
}

function trimTrailingSeps(s_mut) {
    trimTrailingSeps:
    while (true) {
        const s = s_mut;
        if ((s.length > 0) && isSeparator(s[s.length - 1])) {
            s_mut = substring(s, 0, s.length - 1);
            continue trimTrailingSeps;
        }
        else {
            return s;
        }
        break;
    }
}

/**
 * node's path.basename: trailing separators are ignored, then everything after the last one. The
 * root has no basename, so "C:\" and "/" give "".
 */
export function basename(p) {
    const rest = trimTrailingSeps(substring(p, driveRootLength(p)));
    const matchValue = lastSepIndex(rest, rest.length - 1) | 0;
    if (matchValue === -1) {
        return rest;
    }
    else {
        return substring(rest, matchValue + 1);
    }
}

/**
 * node's path.dirname. Separators inside the path are left exactly as they were found - node does
 * not normalise here, and neither does this.
 */
export function dirname(p) {
    const rLen = rootLength(p) | 0;
    const root = substring(p, 0, rLen);
    const rest = trimTrailingSeps(substring(p, rLen));
    const matchValue = lastSepIndex(rest, rest.length - 1) | 0;
    if (matchValue === -1) {
        if (rLen > 0) {
            return root;
        }
        else {
            return ".";
        }
    }
    else {
        const head = substring(rest, 0, matchValue);
        if (rLen > 0) {
            return root + head;
        }
        else if (head === "") {
            return sepStr;
        }
        else {
            return head;
        }
    }
}

/**
 * node's path.extname: from the last dot in the basename, when that dot is not the first character.
 * So "a.txt" gives ".txt", "a.b.c" gives ".c", and a dotfile like ".gitignore" gives "".
 */
export function extname(p) {
    const b = basename(p);
    const lastDot = (i_mut) => {
        lastDot:
        while (true) {
            const i = i_mut;
            if (i <= 0) {
                return -1;
            }
            else if (b[i] === ".") {
                return i | 0;
            }
            else {
                i_mut = (i - 1);
                continue lastDot;
            }
            break;
        }
    };
    if ((b !== "") && forAll((c) => (c === "."), b.split(""))) {
        return "";
    }
    else {
        const matchValue = lastDot(b.length - 1) | 0;
        if (matchValue === -1) {
            return "";
        }
        else {
            return substring(b, matchValue);
        }
    }
}

/**
 * Whether `candidate` is `root` or sits somewhere under it.
 * 
 * The separator at the end of the root is what makes this an answer about containment rather than
 * about spelling: without it "C:\proj" contains "C:\projects-elsewhere". Both sides are normalised
 * first, so a `..` or a doubled separator cannot walk out of a root that textually still matches.
 * 
 * Case-folded on Windows, where the filesystem is, and not on POSIX, where it is not. Main has its
 * own copy of this for the paths it confines (Main/Bridge.fs) - that one guards the process
 * boundary and must not depend on anything the renderer compiles.
 */
export function isWithin(root, candidate) {
    if (root === "") {
        return false;
    }
    else {
        const fold = (p) => {
            if (isWin) {
                return normalise(p).toLowerCase();
            }
            else {
                return normalise(p);
            }
        };
        const r = trimEnd(fold(root), sepChar);
        const c = fold(candidate);
        if (c === r) {
            return true;
        }
        else {
            return c.startsWith(r + sepStr);
        }
    }
}

